import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tournamentApi } from '@/api/tournament';
import { CasinoButton } from '@/components/CasinoButton';
import { SignPanel } from '@/components/SignPanel';
import { useAppConfig } from '@/hooks/useAppConfig';
import { formatDollars } from '@/lib/payouts';
import { paypalLink, venmoLink } from '@/lib/paymentLinks';

export function TournamentJoin() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['tournament', 'active'],
    queryFn: tournamentApi.active,
  });

  const join = useMutation({
    mutationFn: tournamentApi.join,
    onSuccess: async ({ entry }) => {
      await qc.invalidateQueries({ queryKey: ['tournament', 'active'] });
      navigate(`/tournament/active/submit/${entry.id}`);
    },
    onError: (err) => {
      const e = err as { error?: string; max?: number };
      if (e.error === 'max_entries_reached') {
        setError(`You already have ${e.max} entries — that's the max.`);
      } else if (e.error === 'no_active_tournament') {
        setError("There's no active tournament right now.");
      } else if (e.error === 'pending_approval') {
        setError("Your account isn't approved yet.");
      } else {
        setError("Couldn't join. Try again in a moment.");
      }
    },
  });

  const config = useAppConfig();
  const t = q.data?.tournament;
  const existingCount = q.data?.myEntries.length ?? 0;
  const isSecondEntry = existingCount > 0;
  // Entry count for the payment amount: this single buy-in is 1, regardless
  // of how many entries the player already has. (A second buy-in pays one
  // more fee, not 2× the fee.)
  const payInput = t
    ? {
        entryFeeCents: t.entryFeeCents,
        entryCount: 1,
        // Per-tournament machine name wins for the payment note — accurate
        // to the actual cabinet the tournament is running on. The env-level
        // MACHINE_NAME is only the system default for new tournaments.
        machineName: t.machineName || config.machineName,
      }
    : null;
  const venmoUrl = payInput ? venmoLink(config.venmoHandle, payInput) : null;
  const paypalUrl = payInput ? paypalLink(config.paypalHandle, payInput) : null;
  const hasDigitalPay = !!(venmoUrl || paypalUrl);

  return (
    <main className="mx-auto max-w-md px-5 py-6 space-y-5">
      <Link
        to="/tournament/active"
        className="text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
      >
        ← Back to tournament
      </Link>

      {!t ? (
        <p className="text-cabinet-cream/60">Loading…</p>
      ) : (
        <section className="cabinet-card p-6 space-y-5">
          <header>
            <p className="text-xs uppercase tracking-widest text-cabinet-cream/60">
              {t.machineName}
            </p>
            <SignPanel size="md" className="mt-1">
              <h1 className="text-marquee text-2xl uppercase leading-none">
                {t.name}
              </h1>
            </SignPanel>
          </header>

          <div className="rounded-md bg-black/40 ring-1 ring-cabinet-gold/30 px-4 py-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-cabinet-cream/60">
              Entry fee
            </span>
            <span className="font-display text-cabinet-gold-bright text-2xl tabular-nums">
              {formatDollars(t.entryFeeCents)}
            </span>
          </div>

          <p className="text-sm text-cabinet-cream/80 leading-snug">
            {isSecondEntry ? (
              <>
                This will be your <strong>{ordinal(existingCount + 1)}</strong> entry
                (max {t.maxEntriesPerPlayer}). You'll pay another{' '}
                <strong>{formatDollars(t.entryFeeCents)}</strong>
                {hasDigitalPay
                  ? ' — Venmo, PayPal, or cash.'
                  : ' in person to the organizer before scoring.'}
              </>
            ) : (
              <>
                I confirm I'll pay <strong>{formatDollars(t.entryFeeCents)}</strong>{' '}
                {hasDigitalPay
                  ? '— Venmo, PayPal, or cash. The admin will mark me paid once it lands.'
                  : 'in person to the organizer. The admin will mark me as paid before my score counts.'}
              </>
            )}
          </p>

          {hasDigitalPay && (
            <div className="rounded-md bg-cabinet-purple-deep/60 ring-1 ring-cabinet-gold/30 px-4 py-3 space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
                Pay the host
              </p>
              <div className="flex flex-col gap-2">
                {venmoUrl && (
                  <a
                    href={venmoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md bg-[#3D95CE] hover:brightness-110 text-white font-display tracking-widest uppercase text-center py-3"
                  >
                    Venmo {formatDollars(t.entryFeeCents)}
                  </a>
                )}
                {paypalUrl && (
                  <a
                    href={paypalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md bg-[#0070BA] hover:brightness-110 text-white font-display tracking-widest uppercase text-center py-3"
                  >
                    PayPal {formatDollars(t.entryFeeCents)}
                  </a>
                )}
              </div>
              <p className="text-[10px] text-cabinet-cream/50 leading-snug">
                Payment is admin-verified — joining doesn't auto-confirm. The host
                marks you paid in person.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <CasinoButton
              fullWidth
              onClick={() => join.mutate()}
              disabled={join.isPending}
            >
              {join.isPending
                ? 'Joining…'
                : isSecondEntry
                  ? `Add ${ordinal(existingCount + 1)} entry`
                  : `Join — I'll pay ${formatDollars(t.entryFeeCents)}`}
            </CasinoButton>
            <Link
              to="/tournament/active"
              className="block text-center text-sm text-cabinet-cream/60 hover:text-cabinet-cream py-2"
            >
              Cancel
            </Link>
          </div>
        </section>
      )}
    </main>
  );
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
